import { useGlobalDrawer } from "@/core/context/GlobalDrawerProvider"
import makeUseStyles from "@/core/ui/style/util/makeUseStyles"
import { UserCardFragment$key } from "@/product/member/common/card/__generated__/UserCardFragment.graphql"
import ProfileAvatar from "@/user/common/avatar/ProfileAvatar"
import { DiscoText, DiscoTextSkeleton } from "@disco-ui"
import DiscoCardButton from "@disco-ui/button/DiscoCardButton"
import DiscoTag from "@disco-ui/tag/DiscoTag"
import { Theme } from "@material-ui/core"
import { Skeleton } from "@material-ui/lab"
import classnames from "classnames"
import { ReactElement } from "react"
import { useFragment } from "react-relay"
import { graphql } from "relay-runtime"

export interface UserCardProps {
  userKey: UserCardFragment$key
  tags?: ReactElement<any, any>[] | null
  checkbox?: ReactElement<any, any> | null | boolean
  dropdown?: ReactElement<any, any> | null | boolean
  testid?: string
  groovyDepths?: keyof Theme["palette"]["groovyDepths"]
  className?: string
}

function UserCard({
  tags,
  checkbox,
  dropdown,
  testid,
  userKey,
  groovyDepths,
  className,
}: UserCardProps) {
  const user = useFragment<UserCardFragment$key>(
    graphql`
      fragment UserCardFragment on User {
        id
        bio
        fullName
        ...ProfileAvatarFragment
      }
    `,
    userKey
  )
  const settingsDrawer = useGlobalDrawer("profileSettings")
  const classes = useStyles({ groovyDepths })

  return (
    <DiscoCardButton
      onClick={openProfileDrawer}
      data-testid={"UserCard.view-profile-button"}
      className={classnames(classes.cardButton, className)}
    >
      <div data-testid={testid}>
        {/* Checkbox */}
        {checkbox && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
          <div
            className={classes.selectContainer}
            onClick={(event) => event.stopPropagation()}
          >
            {checkbox}
          </div>
        )}

        {/* Dropdown (More Actions Button) */}
        {dropdown && <div className={classes.dropdownContainer}>{dropdown}</div>}

        {/* Avatar */}
        <div className={classes.avatarContainer}>
          <ProfileAvatar userKey={user} size={96} showTestUserTag />
        </div>

        {/* Name */}
        <div className={classes.nameContainer}>
          <DiscoText
            variant={"body-lg-600"}
            data-testid={"ProductMemberCard.name"}
            truncateText={3}
            className={"fs-mask"}
          >
            {user.fullName}
          </DiscoText>
        </div>

        {/* Bio */}
        {user.bio && (
          <div className={classes.bioContainer}>
            <DiscoText
              variant={"body-sm"}
              color={"text.secondary"}
              truncateText={2}
              className={classnames(classes.bio, "fs-mask")}
            >
              {user.bio}
            </DiscoText>
          </div>
        )}

        {/* Membership Tags */}
        {tags && tags.length > 0 && (
          <div className={classes.userTagsContainer}>
            {/* Render up to 4 total tags */}
            {tags.length <= 4 ? (
              tags
            ) : (
              <>
                {tags.slice(0, 3)}
                <DiscoTag name={`+ ${tags.length - 3} more`} />
              </>
            )}
          </div>
        )}
      </div>
    </DiscoCardButton>
  )

  function openProfileDrawer() {
    settingsDrawer.open({
      drawerProfileId: user.id,
      profileSettingsTab: "profile",
    })
  }
}

type StyleProps = {
  groovyDepths: UserCardProps["groovyDepths"]
}

const useStyles = makeUseStyles((theme) => ({
  cardButton: ({ groovyDepths }: StyleProps) => ({
    position: "relative",
    padding: theme.spacing(4, 3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.measure.borderRadius.big,
    border: theme.palette.constants.borderSmall,
    width: "100%",
    height: "100%",
    boxShadow: groovyDepths ? theme.palette.groovyDepths[groovyDepths] : undefined,
    transition: "box-shadow 0.4s ease",
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
    "&:hover": {
      transition: theme.transitions.create(["boxShadow", "cursor"], {
        duration: theme.transitions.duration.standard,
        easing: theme.transitions.easing.easeInOut,
      }),
      cursor: "pointer",
      boxShadow: theme.palette.groovyDepths.insideCard,
      backgroundColor: theme.palette.background.paper,
    },
  }),
  selectContainer: {
    position: "absolute",
    top: 0,
    left: theme.spacing(1),
  },
  dropdownContainer: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  avatarContainer: {
    marginBottom: theme.spacing(2),
    display: "inline-flex",
  },
  nameContainer: {
    marginBottom: theme.spacing(1),
  },
  bioContainer: {
    textAlign: "center",
    color: theme.palette.groovy.grey[400],
    maxHeight: "46px",
  },
  bio: {
    wordBreak: "break-word",
  },
  userTagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    overflow: "hidden",
    maxHeight: "64px", // 2 rows x 28px + 8px gap
    marginTop: theme.spacing(1.5),
  },
}))

const useSkeletonStyles = makeUseStyles((theme) => ({
  skeletonCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.measure.borderRadius.big,
    width: "100%",
    height: 300,
    border: "none",
    transition: "box-shadow 0.4s ease",
  },
}))

export const UserCardSkeleton = () => {
  const classes = useSkeletonStyles()
  return (
    <div className={classes.skeletonCard}>
      <Skeleton variant={"circle"} width={96} height={96} />
      <DiscoTextSkeleton marginTop={4} variant={"body-lg-600"} width={150} />
      <DiscoTextSkeleton width={120} />
      <DiscoTextSkeleton width={130} />
    </div>
  )
}

export default UserCard
